import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap, LayersControl } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format } from 'date-fns';
import L from 'leaflet';
import { supabase } from '../../utils/supabaseClient';
import '../../styles/TrackVehicles.css';

// Fix Leaflet marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png'
});

// Create custom van marker icon
const vanIcon = (color) => new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-' + color + '.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Generate a random color for each van
const generateVanColor = (vanId) => {
  // Predefined colors for better visibility
  const colors = [
    '#FF5733', '#33FF57', '#3357FF', '#FF33A8', 
    '#33A8FF', '#FFA833', '#A833FF', '#FF5733', 
    '#4CAF50', '#2196F3', '#9C27B0', '#FF9800'
  ];
  return colors[vanId % colors.length];
};

// Calculate distance between two coordinates using Haversine formula (in kilometers)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1); 
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2); 
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const d = R * c; // Distance in km
  return d;
};

const deg2rad = (deg) => {
  return deg * (Math.PI/180);
};

// Calculate total distance of a path
const calculateTotalDistance = (coordinates) => {
  let totalDistance = 0;
  
  for (let i = 0; i < coordinates.length - 1; i++) {
    const current = coordinates[i];
    const next = coordinates[i + 1];
    totalDistance += calculateDistance(
      current.latitude, current.longitude,
      next.latitude, next.longitude
    );
  }
  
  return totalDistance.toFixed(2); // Return distance in km with 2 decimal places
};

// Component to fit bounds of the map to all markers
function MapBoundsHandler({ vanPaths }) {
  const map = useMap();
  
  useEffect(() => {
    if (vanPaths.length > 0) {
      // Collect all coordinates from all paths
      const allCoordinates = vanPaths.flatMap(path => 
        path.coordinates.map(coord => [coord.latitude, coord.longitude])
      );
      
      if (allCoordinates.length > 0) {
        // Create bounds and fit map to those bounds
        const bounds = L.latLngBounds(allCoordinates);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [map, vanPaths]);
  
  return null;
}

function TrackVehicles() {
  const [selectedVans, setSelectedVans] = useState([]);
  const [availableVans, setAvailableVans] = useState([]);
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [vanPaths, setVanPaths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Default center of the map (set to a location in India)
  const defaultCenter = [20.5937, 78.9629]; // India's center coordinates
  const mapRef = useRef(null);
  
  // Fetch available vans on component mount
  useEffect(() => {
    fetchVans();
    // Set initial date to today
    fetchVanPaths();
  }, []);
  
  // Fetch van paths when selected vans or date changes
  useEffect(() => {
    fetchVanPaths();
  }, [selectedVans, selectedDate]);
  
  const fetchVans = async () => {
    try {
      const { data, error } = await supabase
        .from('vans')
        .select('*')
        .order('van_no');
        
      if (error) throw error;
      
      setAvailableVans(data || []);
      if (data && data.length > 0) {
        // Initially select all vans
        setSelectedVans(data.map(van => van.id));
      }
    } catch (err) {
      console.error('Error fetching vans:', err);
      setError('Failed to load vans. Please try again.');
    }
  };
  
  const fetchVanPaths = async () => {
    if (selectedVans.length === 0) {
      setVanPaths([]);
      return;
    }
    
    setIsLoading(true);
    try {
      // Calculate start and end of day for the selected date
      const startDate = `${selectedDate}T00:00:00`;
      const endDate = `${selectedDate}T23:59:59`;
      
      // Query for locations of selected vans on the selected date
      const { data, error } = await supabase
        .from('van_locations')
        .select(`
          id,
          van_id,
          latitude,
          longitude,
          timestamp,
          vans(van_no)
        `)
        .in('van_id', selectedVans)
        .gte('timestamp', startDate)
        .lte('timestamp', endDate)
        .order('timestamp', { ascending: true });
        
      if (error) throw error;
      
      // Process the data to create paths for each van
      const pathsByVan = {};
      
      if (data && data.length > 0) {
        data.forEach(location => {
          const { van_id, latitude, longitude, timestamp, vans } = location;
          
          if (!pathsByVan[van_id]) {
            pathsByVan[van_id] = {
              vanId: van_id,
              vanNo: vans?.van_no || `Van ${van_id}`,
              color: generateVanColor(van_id),
              coordinates: []
            };
          }
          
          pathsByVan[van_id].coordinates.push({
            latitude,
            longitude,
            timestamp
          });
        });
        
        // Calculate total distance for each path
        Object.values(pathsByVan).forEach(path => {
          path.totalDistance = calculateTotalDistance(path.coordinates);
        });
        
        // Convert the object to an array
        const paths = Object.values(pathsByVan);
        setVanPaths(paths);
      } else {
        setVanPaths([]);
      }
    } catch (err) {
      console.error('Error fetching van paths:', err);
      setError(`Failed to load van paths: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVanSelection = (vanId) => {
    setSelectedVans(prev => {
      if (prev.includes(vanId)) {
        return prev.filter(id => id !== vanId);
      } else {
        return [...prev, vanId];
      }
    });
  };
  
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };
  
  const handleSelectAllVans = () => {
    setSelectedVans(availableVans.map(van => van.id));
  };
  
  const handleDeselectAllVans = () => {
    setSelectedVans([]);
  };
  
  return (
    <div className="track-vehicles-container">
      <div className="track-vehicles-header">
        <h1>Track Vehicles</h1>
      </div>
      
      <div className="track-vehicles-controls">
        <div className="van-selector">
          <label>Select Vans:</label>
          <div className="van-chips">
            {availableVans.map(van => (
              <div 
                key={van.id} 
                className={`van-chip ${selectedVans.includes(van.id) ? 'selected' : ''}`}
                onClick={() => handleVanSelection(van.id)}
                style={{
                  borderColor: selectedVans.includes(van.id) ? generateVanColor(van.id) : '#ccc'
                }}
              >
                <div 
                  className="color-indicator" 
                  style={{ backgroundColor: generateVanColor(van.id) }}
                ></div>
                <span>Van {van.van_no}</span>
              </div>
            ))}
          </div>
          <div className="van-selector-controls">
            <button onClick={handleSelectAllVans} className="select-button">Select All</button>
            <button onClick={handleDeselectAllVans} className="deselect-button">Deselect All</button>
          </div>
        </div>
        
        <div className="date-selector">
          <label htmlFor="date-picker">Select Date:</label>
          <input 
            type="date" 
            id="date-picker" 
            value={selectedDate} 
            onChange={handleDateChange}
            max={format(new Date(), 'yyyy-MM-dd')} // Can't select future dates
          />
        </div>
      </div>
      
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading van paths...</p>
        </div>
      )}
      
      {error && (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={fetchVanPaths} className="retry-button">Retry</button>
        </div>
      )}
      
      <div className="map-container">
        <MapContainer 
          center={defaultCenter} 
          zoom={5} 
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
        >
          <LayersControl position="topright">
            {/* Base maps */}
            <LayersControl.BaseLayer name="Google Street" checked>
              <TileLayer
                attribution='&copy; Google Maps'
                url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                maxZoom={20}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="Google Satellite">
              <TileLayer
                attribution='&copy; Google Maps'
                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                maxZoom={20}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="Google Hybrid">
              <TileLayer
                attribution='&copy; Google Maps'
                url="https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}"
                maxZoom={20}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="OpenStreetMap Detailed">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="Esri WorldStreetMap">
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer name="Esri WorldImagery">
              <TileLayer
                attribution='&copy; Esri'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          
          {vanPaths.map(path => (
            <React.Fragment key={path.vanId}>
              {path.coordinates.length > 0 && (
                <>
                  <Polyline
                    positions={path.coordinates.map(coord => [coord.latitude, coord.longitude])}
                    color={path.color}
                    weight={4}
                    opacity={0.7}
                  />
                  
                  {/* Start marker */}
                  <Marker 
                    position={[
                      path.coordinates[0].latitude, 
                      path.coordinates[0].longitude
                    ]}
                    icon={vanIcon('green')}
                  >
                    <Popup>
                      <strong>{path.vanNo}</strong><br />
                      <strong>Total Distance:</strong> {path.totalDistance} km<br />
                      <strong>Start:</strong> {new Date(path.coordinates[0].timestamp).toLocaleTimeString()}<br />
                      <strong>Points:</strong> {path.coordinates.length}
                    </Popup>
                  </Marker>
                  
                  {/* End marker */}
                  <Marker 
                    position={[
                      path.coordinates[path.coordinates.length - 1].latitude, 
                      path.coordinates[path.coordinates.length - 1].longitude
                    ]}
                    icon={vanIcon('red')}
                  >
                    <Popup>
                      <strong>{path.vanNo}</strong><br />
                      <strong>Total Distance:</strong> {path.totalDistance} km<br />
                      <strong>End:</strong> {new Date(path.coordinates[path.coordinates.length - 1].timestamp).toLocaleTimeString()}<br />
                      <strong>Duration:</strong> {Math.round((new Date(path.coordinates[path.coordinates.length - 1].timestamp) - new Date(path.coordinates[0].timestamp)) / 60000)} min
                    </Popup>
                  </Marker>
                </>
              )}
            </React.Fragment>
          ))}
          
          <MapBoundsHandler vanPaths={vanPaths} />
        </MapContainer>
      </div>
      
      <div className="track-vehicles-legend">
        <h3>Van Routes</h3>
        <div className="legend-items">
          {vanPaths.map(path => (
            <div key={path.vanId} className="legend-item">
              <div className="legend-color" style={{ backgroundColor: path.color }}></div>
              <div className="legend-details">
                <div className="legend-title">{path.vanNo}</div>
                <div className="legend-stats">
                  <span className="distance-info">{path.totalDistance} km</span>
                  <span className="point-count">{path.coordinates.length} points</span>
                  <span className="time-info">{Math.round((new Date(path.coordinates[path.coordinates.length - 1].timestamp) - new Date(path.coordinates[0].timestamp)) / 60000)} min</span>
                </div>
              </div>
            </div>
          ))}
          {vanPaths.length === 0 && !isLoading && (
            <div className="no-data">No path data available for the selected vans on this date.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrackVehicles;
